import { useEffect, useMemo, useState } from "react";
import { MdOutlineInfo } from "react-icons/md";
import MeritoButton from "../../../../../components/common/MeritoButtons/MeritoButtons";
import StatusBadge from "../../../../../components/common/StatusBadge/StatusBadge";
import type { ChannelCatalogItem, CoverView, DelegationView, InboxGroup } from "../../../api/connectionHub.types";
import { FIX_ACTION, INBOX_ACTION, INBOX_OWNER, INBOX_PROVIDER } from "../../../api/connectionHub.types";
import { CONNECTION_STATUS, CONNECTION_STATUS_PRESENTATION, isFailedStatus, isVerificationStatus } from "../../../constants/connectionStatus.constants";
import { isForwardingOnlyProvider, PROVIDER_SETUP_GUIDES } from "../../../constants/providerSetupGuides.constants";
import { setupStepOf, type SetupStep } from "../../../constants/setupSteps.constants";
import { useForwardingConfirmedHold } from "../../../hooks/useForwardingConfirmedHold";
import { hasForwardingCheckFailed, isCredentialFailure, useRecheck } from "../../../hooks/useRecheck";
import { useSetupLauncher } from "../../../hooks/useSetupLauncher";
import { formatDelegationValidity, isDelegationExpired } from "../../../utils/delegationValidity";
import ConnectionErrorPanel from "../../common/ConnectionErrorPanel/ConnectionErrorPanel";
import InfoBanner from "../../common/InfoBanner/InfoBanner";
import CopyLinkButton from "../../common/CopyLinkButton/CopyLinkButton";
import CoverStatusRow from "../CoverStatusRow/CoverStatusRow";
import ForwardingOnlyPanel from "../ForwardingOnlyPanel/ForwardingOnlyPanel";
import ForwardingPhasePanel from "../ForwardingPhasePanel/ForwardingPhasePanel";
import InboxIdentity from "../InboxIdentity/InboxIdentity";
import OutlookRulesPanel from "../OutlookRulesPanel/OutlookRulesPanel";
import PasswordRetryCard from "../PasswordRetryCard/PasswordRetryCard";
import FilterPhasePanel from "../FilterPhasePanel/FilterPhasePanel";
import InboxRecheck from "../RecheckPanel/InboxRecheck";
import RecheckPanel from "../RecheckPanel/RecheckPanel";
import styles from "./InboxCard.module.scss";

export interface InboxCardProps {
  group: InboxGroup;
  channelsByIpId: Map<string, ChannelCatalogItem>;
  busyAction?: string | null;
  actionsDisabled?: boolean;
  error?: string | null;
  connectionErrors?: Record<string, string>;
  /** `connectionIds` confirms part of the inbox (one Outlook rule); omitted means the whole inbox. */
  onConfirmForwarding: (group: InboxGroup, connectionIds?: string[]) => void;
  onConfirmFilter: (group: InboxGroup, connectionIds?: string[]) => void;
  /** Recheck "I've Set Up Filter / Rules": verify the failed channels straight away (job 14). */
  onVerify: (group: InboxGroup, connectionIds: string[]) => void;
  onSaveAndRetry?: (group: InboxGroup, connectionId: string, password: string) => Promise<boolean>;
  delegation?: DelegationView | null;
  delegationBusy?: boolean;
  delegationError?: string | null;
  onResendDelegation?: () => void;
  onSelectSubBrands?: () => void;
}

const InboxCard = ({
  group,
  channelsByIpId,
  busyAction,
  actionsDisabled = false,
  error,
  connectionErrors = {},
  onConfirmForwarding,
  onConfirmFilter,
  onVerify,
  onSaveAndRetry,
  delegation,
  delegationBusy = false,
  delegationError,
  onResendDelegation,
  onSelectSubBrands,
}: InboxCardProps): JSX.Element => {
  const actions = useMemo(() => new Set(group.actions), [group.actions]);
  const guide = PROVIDER_SETUP_GUIDES[group.inbox.provider];
  const providerName = guide?.displayName ?? "your inbox";
  const delegated = group.inbox.ownerType === INBOX_OWNER.DELEGATE;
  // Once the delegation has loaded it is the fresher source of truth, so a successful resend clears the expired state.
  const linkExpired = delegated && (delegation ? isDelegationExpired(delegation) : group.status === CONNECTION_STATUS.LINK_EXPIRED);
  const status = linkExpired
    ? CONNECTION_STATUS_PRESENTATION[CONNECTION_STATUS.LINK_EXPIRED]
    : CONNECTION_STATUS_PRESENTATION[group.status];
  const validity = delegated
    ? (linkExpired ? "this link has expired" : formatDelegationValidity(delegation?.expiresAt ?? null))
    : null;
  const forwardingOnly = isForwardingOnlyProvider(group.inbox.provider);
  const forwardingCheckFailed = hasForwardingCheckFailed(group);
  const existingForwarding = !delegated && group.inbox.provider === INBOX_PROVIDER.GMAIL &&
    group.status === CONNECTION_STATUS.FORWARDING_EXISTING;
  const [forwardingKept, setForwardingKept] = useState(false);
  useEffect(() => { setForwardingKept(false); }, [group.status]);
  const existingCheck = existingForwarding && !forwardingKept;
  const forwardingPhase = !forwardingOnly && (
    group.status === CONNECTION_STATUS.FORWARDING_REQUIRED ||
    group.status === CONNECTION_STATUS.CONFIRMING_FORWARDING ||
    forwardingCheckFailed
  );
  // A failed inbox is fixed through the recheck below, not the first-time filter panel.
  const filterPhase = !forwardingOnly && (
    group.status === CONNECTION_STATUS.FORWARDING_CONFIRMED ||
    group.status === CONNECTION_STATUS.UPDATE_FILTER ||
    group.status === CONNECTION_STATUS.FORWARDING_FILTER_CONFIRMED ||
    (existingForwarding && forwardingKept)
  );
  // Outlook is one rule that both matches and redirects, so it has no separate filter phase.
  const outlook = group.inbox.provider === INBOX_PROVIDER.OUTLOOK;
  // Outlook rules are confirmed one at a time, so keep the panel while any channel still waits on its rule,
  // whatever the inbox-level status rolls up to after the first one.
  const showOutlookRules = !delegated && outlook && (
    (forwardingPhase && group.status !== CONNECTION_STATUS.CONFIRMING_FORWARDING) ||
    group.covers.some((cover) => cover.status === CONNECTION_STATUS.FORWARDING_REQUIRED)
  );
  // A forwarding failure is the inbox's (one email's forwarding), so it shows only while the inbox is on
  // its forwarding step. A verification failure belongs to each channel and shows on its row instead.
  const inboxError = error || (!delegated && (forwardingPhase || showOutlookRules || existingCheck) ? group.inbox.forwardingError : null);
  // CONFIRMING_FORWARDING is in forwardingPhase but renders no panel, so its error has no other home.
  const credentialFailures = forwardingOnly && !delegated && onSaveAndRetry ? group.covers.filter(isCredentialFailure) : [];
  const retryCovers = group.covers.filter((cover) => isFailedStatus(cover.status) && !credentialFailures.includes(cover));
  const showForwardingOnly = !delegated && forwardingOnly && (
    group.status === CONNECTION_STATUS.FORWARDING_REQUIRED ||
    group.status === CONNECTION_STATUS.FORWARDING_CONFIRMED ||
    group.status === CONNECTION_STATUS.UPDATE_FILTER ||
    retryCovers.length > 0
  );
  const hostsError = (forwardingPhase && group.status !== CONNECTION_STATUS.CONFIRMING_FORWARDING) || filterPhase ||
    showOutlookRules || showForwardingOnly || existingCheck;
  // A failed verification walks the user back through *checking* their setup (Figma 733:61698 …
  // 809:75102) instead of the first-time panels; password channels see the credentials page first.
  const recheck = useRecheck(group, !delegated);
  const recheckPhase = recheck.phase;
  const inRecheckPanel = recheckPhase !== null;
  const showRows = !delegated && (isVerificationStatus(group.status) || group.covers.some((cover) => isVerificationStatus(cover.status)));
  const verificationInFlight = group.covers.some((cover) => cover.status === CONNECTION_STATUS.VERIFYING);
  const badgeSpinner = group.status === CONNECTION_STATUS.VERIFYING ? verificationInFlight : status.spinner;
  const forwardingHold = useForwardingConfirmedHold();
  const badge = forwardingHold.holding && !inboxError
    ? { label: "Forwarding confirmed", variant: "amber" as const, spinner: false }
    : { ...status, spinner: badgeSpinner };
  const activeStep: SetupStep | null = delegated ? null
    : recheckPhase ? setupStepOf(recheckPhase)
    : existingCheck ? "forwarding"
    : showOutlookRules || filterPhase ? "filter"
    : forwardingPhase ? "forwarding"
    : null;
  const launcher = useSetupLauncher(group, activeStep);

  // Failures need no button: a password channel opens the credentials page on its own and the
  // recheck is already on this card. Only Resend remains.
  const renderSubBrandAction = (cover: CoverView): JSX.Element | null => {
    if (cover.status !== CONNECTION_STATUS.SUB_BRANDS_PENDING || !onSelectSubBrands) return null;
    return (
      <MeritoButton type="secondary-2" size="md" className={styles.cardAction} btnText="Select Sub-Brands" disabled={actionsDisabled} onClick={onSelectSubBrands} />
    );
  };

  const renderFixAction = (cover: CoverView): JSX.Element | null => {
    if (cover.fixPath?.action !== FIX_ACTION.RESEND_LINK || !onResendDelegation) return null;
    return (
      <MeritoButton type="secondary-2" size="md" className={styles.cardAction} btnText="Resend" loading={delegationBusy} disabled={actionsDisabled} onClick={onResendDelegation} />
    );
  };

  return (
    <article className={`${styles.card} ${inboxError ? styles.errorCard : ""}`}>
      <header className={styles.header}>
        <InboxIdentity
          provider={group.inbox.provider}
          email={group.inbox.email}
          covers={showRows ? undefined : group.covers.map((cover) => ({
            key: cover.connectionId,
            logo: channelsByIpId.get(cover.ipId)?.logo ?? cover.logo,
            name: cover.ipName,
          }))}
        />
        <StatusBadge label={badge.label} variant={badge.variant} spinner={badge.spinner} />
      </header>

      {launcher.blockedLabel ? (
        <InfoBanner variant="informative" icon={<MdOutlineInfo />} className={styles.pipNotice}>
          A floating window opened on your screen. Use &quot;<button type="button" className={styles.pipLink} onClick={launcher.openBlocked}>{launcher.blockedLabel}</button>&quot; there to continue.
        </InfoBanner>
      ) : null}

      {showRows ? (
        <div className={styles.coverRows}>
          {group.covers.map((cover) => (
            <CoverStatusRow
              key={cover.connectionId}
              cover={cover}
              channel={channelsByIpId.get(cover.ipId)}
              error={connectionErrors[cover.connectionId] || null}
              spinner={cover.status === CONNECTION_STATUS.VERIFYING ? true : undefined}
              action={isFailedStatus(cover.status) ? renderFixAction(cover) : renderSubBrandAction(cover)}
            />
          ))}
        </div>
      ) : null}

      {delegated ? (
        <div className={styles.content}>
          <p>
            A one-time setup link has been sent to <strong>{delegation?.inboxEmail ?? group.inbox.email}{validity ? " -" : ""}</strong>
            {validity ? ` ${validity}` : ""}.{" "}
            {linkExpired ? "Send a new one to continue." : "No Merito account needed to use it."}
          </p>
          <div className={styles.actions}>
            {actions.has(INBOX_ACTION.RESEND_LINK) && onResendDelegation ? (
              <MeritoButton type="secondary-2" size="md" className={styles.cardAction} btnText={linkExpired ? "Send New Link" : "Resend"} loading={delegationBusy} disabled={actionsDisabled} onClick={onResendDelegation} />
            ) : null}
            {actions.has(INBOX_ACTION.COPY_LINK) ? (
              <CopyLinkButton
                value={linkExpired ? null : delegation?.shareUrl ?? null}
                label={linkExpired ? "Copy New Link" : "Copy Link"}
                disabled={actionsDisabled}
                className={styles.cardAction}
              />
            ) : null}
          </div>
          <ConnectionErrorPanel error={delegationError} />
        </div>
      ) : null}

      <InboxRecheck
        group={group}
        recheck={recheck}
        launcher={launcher}
        error={inboxError}
        disabled={actionsDisabled}
        busy={busyAction === "forwarding" || busyAction === "filter" || busyAction === "verify"}
        onConfirmForwarding={(connectionIds) => onConfirmForwarding(group, connectionIds)}
        onConfirmFilter={(connectionIds) => onConfirmFilter(group, connectionIds)}
        onVerify={(connectionIds) => onVerify(group, connectionIds)}
      />

      {showOutlookRules && !inRecheckPanel ? (
        <OutlookRulesPanel
          error={inboxError}
          rules={group.rules}
          covers={group.covers}
          channelsByIpId={channelsByIpId}
          guide={guide}
          accountEmail={group.inbox.email}
          settingsUrl={launcher.urlFor("filter")}
          // Keyed on the rules, not the status: confirming one rule must not send the user back to the start.
          resetKey={group.rules.map((rule) => rule.name).join("|")}
          canOpen={actions.has(INBOX_ACTION.OPEN_FORWARDING_SETTINGS)}
          canCopy={actions.has(INBOX_ACTION.COPY_LINK)}
          canConfirm={actions.has(INBOX_ACTION.CONFIRM_FORWARDING)}
          disabled={actionsDisabled}
          busy={busyAction === "forwarding"}
          onOpen={() => launcher.open("filter")}
          onConfirmRule={(connectionIds) => onConfirmForwarding(group, connectionIds)}
        />
      ) : null}

      {!delegated && !inRecheckPanel && forwardingPhase && group.status !== CONNECTION_STATUS.CONFIRMING_FORWARDING && !outlook ? (
        <ForwardingPhasePanel
          error={inboxError}
          keepInstruction={forwardingCheckFailed}
          providerName={providerName}
          accountEmail={group.inbox.email}
          resetKey={group.status}
          forwardingAddress={group.forwardingAddress}
          guide={guide}
          settingsUrl={launcher.urlFor("forwarding")}
          canOpen={actions.has(INBOX_ACTION.OPEN_FORWARDING_SETTINGS)}
          canCopy={actions.has(INBOX_ACTION.COPY_LINK)}
          canConfirm={actions.has(INBOX_ACTION.CONFIRM_FORWARDING)}
          disabled={actionsDisabled}
          busy={busyAction === "forwarding"}
          onOpen={() => launcher.open("forwarding")}
          onConfirm={() => onConfirmForwarding(group)}
        />
      ) : null}

      {credentialFailures.map((cover) => (
        <PasswordRetryCard
          key={cover.connectionId}
          cover={cover}
          disabled={actionsDisabled}
          onSaveAndRetry={(password) => onSaveAndRetry?.(group, cover.connectionId, password) ?? Promise.resolve(false)}
        />
      ))}

      {showForwardingOnly ? (
        <ForwardingOnlyPanel
          forwardingAddress={group.forwardingAddress}
          filterFrom={group.links.filterFrom}
          filterHasWords={group.links.filterHasWords}
          guide={guide}
          error={inboxError || retryCovers[0]?.lastError || null}
          canConfirm={actions.has(INBOX_ACTION.CONFIRM_FORWARDING)}
          disabled={actionsDisabled}
          busy={busyAction === "forwarding"}
          onConfirm={() => {
            forwardingHold.hold();
            onConfirmForwarding(group, retryCovers.length ? retryCovers.map((cover) => cover.connectionId) : undefined);
          }}
        />
      ) : null}

      {!delegated && group.status === CONNECTION_STATUS.CONFIRMING_FORWARDING && !outlook && !forwardingOnly ? (
        <RecheckPanel
          phase="forwarding"
          openDirectly
          providerName={providerName}
          accountEmail={group.inbox.email}
          forwardingAddress={group.forwardingAddress}
          settingsUrl={launcher.urlFor("forwarding")}
          disabled={actionsDisabled || !actions.has(INBOX_ACTION.OPEN_FORWARDING_SETTINGS) || !launcher.urlFor("forwarding")}
          onOpen={() => launcher.open("forwarding")}
        />
      ) : null}

      {existingCheck && !inRecheckPanel ? (
        <RecheckPanel
          phase="forwarding"
          providerName={providerName}
          accountEmail={group.inbox.email}
          forwardingAddress={group.forwardingAddress}
          settingsUrl={launcher.urlFor("forwarding")}
          guide={guide}
          error={inboxError}
          canOpen={actions.has(INBOX_ACTION.OPEN_FORWARDING_SETTINGS)}
          canCopy={actions.has(INBOX_ACTION.COPY_LINK)}
          canConfirm={actions.has(INBOX_ACTION.CONFIRM_FORWARDING)}
          disabled={actionsDisabled}
          busy={busyAction === "forwarding"}
          onOpen={() => launcher.open("forwarding")}
          onSkip={() => setForwardingKept(true)}
          onConfirmUpdated={() => onConfirmForwarding(group)}
        />
      ) : null}

      {!delegated && !inRecheckPanel && filterPhase && !outlook ? (
        <FilterPhasePanel
          forwardingAddress={group.forwardingAddress}
          error={inboxError}
          providerName={providerName}
          accountEmail={group.inbox.email}
          resetKey={group.status}
          description="One combined filter covers every channel on this inbox."
          guide={guide}
          settingsUrl={launcher.urlFor("filter")}
          canOpen={actions.has(INBOX_ACTION.OPEN_FILTER_SETTINGS)}
          canCopy={actions.has(INBOX_ACTION.COPY_LINK)}
          canConfirm={actions.has(INBOX_ACTION.CONFIRM_FILTER)}
          disabled={actionsDisabled}
          busy={busyAction === "filter"}
          onOpen={() => launcher.open("filter")}
          onConfirm={() => onConfirmFilter(group)}
        />
      ) : null}

      {/* The two phase panels host the error themselves; every other state needs it rendered here. */}
      {inboxError && !hostsError && !inRecheckPanel ? <ConnectionErrorPanel error={inboxError} /> : null}
      {launcher.pip}
    </article>
  );
};

export default InboxCard;
